<template>
  <div
    id="diagnostics"
    class="pa-4"
  >
    <v-container
      v-if="!diagnosticStats.data"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="100"
      />
    </v-container>

    <div v-else>
      <v-row
        v-if="events.selected"
        key="diagnosticsPortalFilter"
        align="center"
        dense
        class="mt-0"
      >
        <v-col>
          <span
            class="text-h6 font-weight-medium text-none primary--text"
            style="cursor: pointer;"
            @click="eventSelection = true"
          >
            {{ events.selected.title }} ({{ events.selected.diagnostic_protocol.name }})
            <v-icon
              x-small
              color="primary"
            >fa fa-chevron-down</v-icon>
          </span>
          <v-row
            align="center"
            no-gutters
          >
            <v-col cols="auto">
              {{ dayjs(events.selected.start_at).format(getDateTimeFormatForUser('dddd, MMMM D HH:mm')) }} - {{ dayjs(events.selected.end_at).format(getDateTimeFormatForUser('HH:mm')) }}
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-chip
                label
                :color="eventTypes[events.selected.event_type].color"
                dark
                small
              >
                {{ eventTypes[events.selected.event_type].text }}
              </v-chip>
            </v-col>
            <v-col
              cols="auto"
              class="ml-2"
            >
              <v-btn
                small
                text
                color="primary"
                class="mr-2 text-none"
                @click="eventSidebar = !eventSidebar"
              >
                {{ $t('button.view_event_details') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          v-if="diagnosticStats.data && diagnosticStats.data.length > 1"
          cols="auto"
        >
          <v-select
            v-model="view.selected"
            :items="view.options"
            item-text="text"
            item-value="value"
            hide-details
            solo
            dense
            style="width: 250px"
          />
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <div
        v-if="diagnosticStats.data.length === 0"
        class="d-flex justify-center pt-5"
      >
        {{ $t('data_empty.diagnostic_stats') }}
      </div>

      <div v-else-if="diagnosticStats.data.length === 1">
        <div class="mb-4 d-flex align-center">
          <v-avatar
            size="70px"
            class="elevation-1"
            style="border: 1px solid black;"
          >
            <v-img
              :src="player.photo_url"
              width="70px"
              height="70px"
              class="rounded"
            >
              <template v-slot:placeholder>
                <v-icon x-large>
                  fal fa-user
                </v-icon>
              </template>
            </v-img>
          </v-avatar>
          <span class="ml-3 text-h6 font-weight-medium"> {{ player.first_name }} {{ player.last_name }}</span>
          <v-btn
            class="text-none ml-3"
            color="primary"
            small
            text
            @click="selectedPlayer = player; individualModal = true"
          >
            {{ $t("button.view_player_diagnostic_profile") }}
          </v-btn>
        </div>

        <div
          v-for="test in Object.keys(playerStatsGroupedByTest)"
          :key="test"
          class="my-6"
        >
          <subtitle class="pb-3">
            {{ test }}
          </subtitle>
          <v-row>
            <v-col
              v-for="variable in playerStatsGroupedByTest[test]"
              :key="variable.var_id"
              cols="3"
            >
              <stat-card
                :loading="diagnosticStats.loading"
                :title="variable.var"
                :value="variable.value + ' ' + getUnitLabel(variable.unit)"
              />
            </v-col>
          </v-row>
        </div>
      </div>

      <div v-else>
        <v-card class="rounded-lg">
          <diagnostic-results-table
            :diagnostic-stats="diagnosticStats.data"
            :loading="diagnosticStats.loading"
            :available-variables="variables.data"
            :selected-variables="variables.selected"
            :view-type="view.selected"
            @select:player="selectedPlayer = $event; individualModal = true"
            @update:columns="variables.selected = $event"
          />
        </v-card>

        <line-chart
          class="mt-5"
          :raw-data="chartRawData"
          :chart-value-keys="chartValueKeys"
          y-value-prefix="diagnostic_stats."
          :xaxis="{
            labels: {
              rotate: -45
            },
            categories: chartRawData.map(p => p.name),
            tickPlacement: 'between'
          }"
          :fill="{ type: 'solid', opacity: 0.5 }"
          :tooltip="{
            y: {
              formatter: (value, { seriesIndex, w }) => {
                const seriesKey = w.config.series[seriesIndex].name;
                const chartKey = chartValueKeys.find(k => k.label === seriesKey);
                let unit = '';
                if (view.selected === 'norm_value' || view.selected === 'norm_z_score') {
                  unit = '%';
                } else if (view.selected === 'z_score') {
                  unit = '';
                } else {
                  unit = chartKey ? chartKey.unit : '';
                }
                return `${value}${unit ? unit : ''}`;
              }
            }
          }"
        >
          <template v-slot:header>
            <v-card-title class="pa-6">
              <v-row>
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('card_title.participant_comparison') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-select
                    v-model="variables.graphSelected"
                    :items="variables.data"
                    hide-details
                    outlined
                    item-text="var_name"
                    return-object
                    dense
                    style="width: 375px"
                  />
                </v-col>
              </v-row>
            </v-card-title>
          </template>
        </line-chart>
      </div>

      <select-test-session-dialog
        key="diagnosticView"
        :visible.sync="eventSelection"
        :filters="filters"
        :events="events"
        :protocols="diagnosticProtocols.data"
        @select:event="events.selected = $event; fetchDiagnosticEventStats(); eventSelection = false"
        @update:filter="fetchEvents"
        @click:addNewTestSession="addNewTestSession"
      />

      <card-dialog
        v-if="selectedPlayer"
        v-model="individualModal"
        :title="selectedPlayer.name"
        fullscreen
      >
        <v-card-text
          class="pa-6 grey-bg"
        >
          <player-diagnostic-view
            :key="selectedPlayer.id"
            :player-prop="selectedPlayer"
            :protocol-id="events.selected.diagnostic_protocol.id"
          />
        </v-card-text>
      </card-dialog>
    </div>

    <event-sidebar
      v-if="eventDetails.data && eventSidebar"
      :event-view="false"
      :event-details="eventDetails.data"
      :loading="eventDetails.loading"
      :value.sync="eventSidebar"
    />
  </div>
</template>

<script>
import {
  DiagnosticProtocolApiService,
  EventApiService,
  PlayerPositionApiService,
  PlayerStatusApiService
} from '@/data/services'
import { getDateTimeFormatForUser, toCamelCase } from '@/utils/helpers'
import { EventType, EventTypes } from '@/data/models'
import dayjs from 'dayjs'
import i18n from '@/plugins/i18n'
import SelectTestSessionDialog from '@/components/dialogs/SelectTestSessionDialog.vue'
import { EventBus } from '@/plugins/bus'
import LineChart from '@/components/charts/LineChart.vue'
import StatCard from '@/components/StatCard.vue'
import { groupBy } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import PlayerDiagnosticView from '@/views/player/PlayerDiagnosticView.vue'
import EventSidebar from '@/components/events/EventSidebar.vue'
import DiagnosticResultsTable from '@/components/diagnostics/DiagnosticResultsTable.vue'

export default {
  name: 'DiagnosticsView',
  components: {
    DiagnosticResultsTable,
    EventSidebar,
    PlayerDiagnosticView,
    CardDialog,
    Subtitle,
    StatCard,
    LineChart,
    SelectTestSessionDialog
  },
  data: () => ({
    diagnosticProtocols: {
      loading: false,
      data: [],
      selected: undefined
    },
    events: {
      loading: false,
      data: [],
      selected: undefined
    },
    eventDetails: {
      data: {},
      loading: false
    },
    diagnosticStats: {
      data: null,
      loading: false
    },
    variables: {
      data: [],
      selected: [],
      graphSelected: null
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    eventSelection: false,
    individualModal: false,
    selectedPlayer: null,
    eventSidebar: false,
    filters: {
      eventType: undefined,
      dateFrom: dayjs().subtract(2, 'month').format('YYYY-MM-DD'),
      dateTo: dayjs().format('YYYY-MM-DD'),
      name: '',
      diagnosticProtocolId: null
    },
    view: {
      selected: 'value',
      options: [
        { text: i18n.t('diagnostics.measurement_analysis.views.values'), value: 'value' },
        { text: i18n.t('diagnostics.measurement_analysis.views.normalized_values'), value: 'norm_value' },
        { text: i18n.t('diagnostics.measurement_analysis.views.zscore'), value: 'z_score' },
        { text: i18n.t('diagnostics.measurement_analysis.views.normalized_zscore'), value: 'norm_z_score' }
      ]
    }
  }),
  computed: {
    dayjs() {
      return dayjs
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    chartValueKeys() {
      if (!this.variables.graphSelected) return []

      return [{ key: this.variables.graphSelected.var_id, type: 'bar', label: this.variables.graphSelected.var_name, unit: this.variables.graphSelected.unit }]
    },
    chartRawData() {
      return this.diagnosticStats.data.map(stat => {
        return {
          diagnostic_stats: stat.variables.reduce((acc, variable) => {
            acc[variable.var_id] = variable[this.view.selected]
            return acc
          }, {}),
          name: stat.player.name
        }
      })
    },
    player() {
      return this.diagnosticStats.data[0].player
    },
    playerStatsGroupedByTest() {
      return groupBy(this.diagnosticStats.data[0].variables, 'test_name')
    }
  },
  watch: {
    'currentTeam.id': function () {
      this.fetchEventDetails()
    },
    eventSidebar(val) {
      if (val) this.fetchEventDetails()
    }
  },
  mounted() {
    this.eventTypes = EventTypes

    this.fetchDiagnosticProtocols()
    this.fetchEvents()

    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchDiagnosticProtocols() {
      try {
        this.diagnosticProtocols.loading = true

        const result = await DiagnosticProtocolApiService.getAll({ order_by: 'name' })

        this.diagnosticProtocols.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.diagnosticProtocols.loading = false
      }
    },
    async fetchEvents() {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          types: [EventType.TEST_SESSION, EventType.READINESS],
          search: this.filters.name,
          type: this.filters.eventType,
          from: this.filters.dateFrom,
          to: this.filters.dateTo,
          diagnosticProtocolId: this.filters.diagnosticProtocolId
        })

        this.events.data = response.data

        if (!this.events.selected) {
          this.events.selected = response.data[0]
          await this.fetchDiagnosticEventStats()
        }
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    async fetchDiagnosticEventStats() {
      this.diagnosticStats.loading = true

      try {
        const response = await EventApiService.getDiagnosticStats(this.events.selected.id)

        this.diagnosticStats.data = response

        this.variables.data = response.map(stat => stat.variables).flat().reduce((acc, variable) => {
          if (!acc.some(v => v.var_id === variable.var_id)) {
            acc.push(variable)
          }
          return acc
        }, [])
        this.variables.selected = this.variables.data.slice(0, 3)
        this.variables.graphSelected = this.variables.data[0]
      } catch (e) {
        this.diagnosticStats.data = []
      } finally {
        this.diagnosticStats.loading = false
      }
    },
    async fetchEventDetails() {
      this.eventDetails.loading = true

      try {
        const response = await EventApiService.getEventDetails(this.events.selected.id, {})

        this.eventDetails.data = response.data
      } catch (e) {

      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll({ sport_id: this.currentTeam.sport.id })

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    addNewTestSession() {
      EventBus.$emit(this.$events.OPEN_DIALOG_EVENT_FORM, { eventType: EventType.TEST_SESSION })
    },
    getUnitLabel(unit, withParentheses = false) {
      if (unit === null) return ''
      if (['text', 'grade'].includes(unit)) return ''

      if (withParentheses) {
        return ` (${unit})`
      } else {
        return ` ${unit}`
      }
    }
  }
}
</script>
